import { cn } from '@invent/wl-ui-kit-next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@invent/wl-ui-kit-next/ui';
import { Ban, Check, CircleAlert, CircleCheck, CircleDot, CirclePause, LoaderCircle } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { HelpPopover, PageHeader, StatusChip, type ChipTone } from '@/design';
import { useCan, type Permission } from '@/domains/auth';
import {
  homeApi,
  type IDashboard,
  type IDashboardActivity,
  type IDashboardCycle,
  type IDashboardFreshness,
  type IDashboardPipeline,
  type IDashboardQueues,
  type IDashboardRanking,
  type IDashboardWorkload
} from '../api/home-api';
import { LIST_SURVEYS, changedWhen, importStateChip, listSurveyLabel } from '../lists';
import { CHECK_REASONS, checkReasonLabel } from '../number-checks';
import { RankingStatusChip } from '../pages/rankings/editions';
import { WorkPageHeaderActions, useReadAt } from '../pages/lists/work-page/header-actions';
import { TEAM_PATHS } from '../paths';
import { refusalMessage } from '../refusal';
import { useAdminData } from './admin-page';
import { InlineBar, LineKey, Sparkline, type ISparkSeries } from './dashboard-charts';
import { formatDay } from './days';
import { SignalBadge, StateCards, type IStateCard, type IStateSignal, type SignalTone } from './state-cards';

/**
 * The Team Portal's Home as a team dashboard (SR-485, SR-486) — the same for everyone, from
 * survey-api's one dashboard read, laid out to fit one screen: the header with the cycle and its
 * next milestone; the viewer's own tasks and the work queues as the work pages' own cards; the
 * cycle's milestones as a timeline; each survey's pipeline as a table beside the last 14 days'
 * activity; who holds what beside the viewer's recent records (the caller's), how fresh the data is
 * and the rankings. What each block means is under the header's "?", not in captions.
 *
 * Every number is survey-api's. While the read is out the blocks say so in text; a failed read
 * says why, and no block draws a `0` it did not read. *Refresh* reads again and keeps the last
 * answer on screen meanwhile.
 *
 * **A link is drawn only where its route opens for the viewer** (`OPENS`, the permission each
 * route's guard and the rail's entry name beyond Home's own): without it a count is plain text, and
 * the DD calls card, which the cards draw as a link, is not drawn.
 */

/** DD calls over every survey's DD list — what the card counts (SR-499); a survey this deployment
 *  does not hold is left out by the page. */
const DD_EVERY_SURVEY = LIST_SURVEYS.map((survey) => `survey=${encodeURIComponent(survey.code)}`).join('&');

/** The permission each route the dashboard links to needs beyond Home's `responses.browse` —
 *  `nav.ts`'s entries and the routes' guards. */
const OPENS = {
  ddCalls: 'internal_fields.read',
  rankings: 'internal_fields.read',
  refreshRuns: 'integrations.configure',
  imports: 'firm_exchange.manage'
} as const satisfies Record<string, Permission>;
export function HomeDashboard({ recent }: { recent: React.ReactNode }) {
  const { data, error, reload, reading } = useAdminData<IDashboard>(async (signal) => {
    try {
      return await homeApi.dashboard(signal);
    } catch (readError) {
      throw new Error(refusalMessage(readError, 'Could not read the dashboard'));
    }
  }, []);
  const readAt = useReadAt(data);
  const cycle = data?.cycle ?? null;

  return (
    <>
      <PageHeader
        title="Home"
        row
        tabs={cycle && <CycleSummary cycle={cycle} />}
        actions={
          <WorkPageHeaderActions
            summary={null}
            refresh={{ reading, readAt, onClick: () => void reload() }}
            help={<DashboardHelp />}
          />
        }
      />
      <div
        className={cn(
          'grid grid-cols-1 gap-3 px-6 py-3 xl:grid-cols-3',
          reading && data !== null && 'opacity-60 transition-opacity'
        )}
      >
        {data === null ? (
          <p
            role={error ? 'alert' : undefined}
            className="rounded-lg border border-border bg-white px-4 py-3 text-muted-foreground xl:col-span-3"
          >
            {error ? `The dashboard could not be read. ${error}` : 'Reading the dashboard…'}
          </p>
        ) : (
          <>
            <section aria-label="Team queues" className="xl:col-span-3">
              <QueueCards queues={data.queues} mine={data.mine} />
            </section>
            {cycle && (
              <Block id="home-cycle" title={cycle.name} className="xl:col-span-3">
                <CycleTimeline cycle={cycle} />
              </Block>
            )}
            <Block id="home-pipeline" title="Pipeline by survey" className="xl:col-span-2">
              <Pipeline rows={data.pipeline} />
            </Block>
            <Block id="home-activity" title="Activity, last 14 days">
              <Activity activity={data.activity} />
            </Block>
            <Block id="home-workload" title="Team workload" className="xl:col-span-2">
              <Workload rows={data.workload} />
            </Block>
            <div className="grid content-start gap-3">
              <Block id="home-recent" title="Recent records">
                {recent}
              </Block>
              <Block id="home-freshness" title="Data freshness">
                <Freshness freshness={data.freshness} />
              </Block>
              <Block id="home-rankings" title="Rankings">
                <Rankings rows={data.rankings} />
              </Block>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function inDays(days: number): string {
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

/** The cycle's name and its next milestone, beside the title. */
function CycleSummary({ cycle }: { cycle: IDashboardCycle }) {
  return (
    <p data-home-cycle="" className="text-[0.8125rem] text-muted-foreground">
      {cycle.name}
      {cycle.next && (
        <>
          <span aria-hidden> · </span>
          next: <span className="font-medium text-foreground">{cycle.next.label}</span>{' '}
          {inDays(cycle.next.daysLeft)}
        </>
      )}
    </p>
  );
}

function Block({
  id,
  title,
  className,
  children
}: {
  id: string;
  title: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby={id} className={cn('min-w-0 rounded-lg border border-border bg-white', className)}>
      <h2 id={id} className="px-4 pt-2.5 pb-1 text-[0.875rem] font-medium text-[var(--color-royal-blue)]">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-4 pb-3 text-[0.8125rem] text-muted-foreground">{children}</p>;
}

// ---- The work queues -----------------------------------------------------------------------------

function signal(key: string, label: string, tone: SignalTone, count: number): IStateSignal {
  return { key, label, tone, count };
}

/** The viewer's own tasks, then the work pages, as their own cards: each the page's count and
 *  signals, and a link to it. Number checks' signals are the unassigned and each reason's, in the
 *  queue's words (SR-487 ruling 16) — every reason survey-api counts, in the filter's order; an
 *  API before SR-487 counts none, and the card is not drawn. */
function QueueCards({ queues, mine }: { queues: IDashboardQueues; mine: IDashboard['mine'] }) {
  const can = useCan();
  const checks = queues.identityChecks;
  const card = (key: string, to: string, label: string, count: number, hint: string, signals: IStateSignal[]): IStateCard => ({
    key,
    to,
    label,
    count: count.toLocaleString('en-US'),
    hint,
    signals,
    current: false,
    data: { 'data-queue': key }
  });
  const cards = [
    card('mine', TEAM_PATHS.tasks, 'My tasks', mine.tasks, 'assigned to you', [
      signal('overdue', 'overdue', 'danger', mine.overdue)
    ]),
    card('compliance', TEAM_PATHS.compliance, 'Compliance', queues.compliance.open, 'open work items', [
      signal('overdue', 'overdue', 'danger', queues.compliance.overdue),
      signal('unassigned', 'unassigned', 'info', queues.compliance.unassigned)
    ]),
    ...(checks
      ? [
          card('identity_check', TEAM_PATHS.numberChecks, 'Number checks', checks.open, 'numbers to confirm', [
            signal('unassigned', 'unassigned', 'info', checks.unassigned),
            ...reasonsOf(checks.byReason).map(([reason, count]) =>
              signal(`reason_${reason}`, asSignal(checkReasonLabel(reason)), 'attention', count)
            )
          ])
        ]
      : []),
    ...(can(OPENS.ddCalls)
      ? [
          card('dd_call', `${TEAM_PATHS.ddCalls}?whose=all&${DD_EVERY_SURVEY}`, 'DD calls', queues.ddCalls.total, 'on the DD lists', [
            signal('overdue', 'overdue', 'danger', queues.ddCalls.overdue),
            signal('unassigned', 'unassigned', 'info', queues.ddCalls.unassigned)
          ])
        ]
      : []),
    card('reinstatement', TEAM_PATHS.reinstatement, 'Reinstatement', queues.reinstatement.total, 'to reinstate', [
      signal('edited_this_cycle', 'edited this cycle', 'success', queues.reinstatement.editedThisCycle)
    ]),
    card('metrics', TEAM_PATHS.metricsUpdates, 'Metrics updates', queues.metrics.owed, 'owe figures', [
      signal('not_asked', 'not asked yet', 'attention', queues.metrics.notAsked),
      signal('asked_no_reply', 'asked, no reply', 'info', queues.metrics.askedNoReply)
    ])
  ];
  return <StateCards label="Team queues" cards={cards} compact />;
}

/** A label in a signal's lower case — its first letter, unless it opens an acronym ("FINRA"). */
function asSignal(label: string): string {
  return label.charAt(1) === label.charAt(1).toLowerCase()
    ? label.charAt(0).toLowerCase() + label.slice(1)
    : label;
}

/** The reasons survey-api counted, in the queue filter's order, then any it names that this side
 *  has no words for, as spelled. */
function reasonsOf(byReason: Record<string, number>): [string, number][] {
  const known: string[] = CHECK_REASONS.map((reason) => reason.value);
  return [
    ...known.filter((reason) => reason in byReason),
    ...Object.keys(byReason).filter((reason) => !known.includes(reason))
  ].map((reason) => [reason, byReason[reason]]);
}

// ---- The cycle's milestones ----------------------------------------------------------------------

type MilestoneState = 'done' | 'next' | 'upcoming';

/** Today as `yyyy-MM-dd` on the viewer's calendar — a milestone's day is compared as a day. */
function today(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/**
 * The milestones as a timeline: a mark, the label and its date under each, the line solid up to the
 * last one done. With no date set on any, each says so, and an account that can set them is shown
 * where.
 */
function CycleTimeline({ cycle }: { cycle: IDashboardCycle }) {
  const can = useCan();
  const day = today();
  const firstAhead = cycle.milestones.findIndex((milestone) => milestone.date !== null && milestone.date >= day);
  const states: MilestoneState[] = cycle.milestones.map((milestone, index) =>
    index === firstAhead ? 'next' : milestone.date !== null && milestone.date < day ? 'done' : 'upcoming'
  );
  if (cycle.milestones.length === 0) return <Empty>This cycle sets no milestones.</Empty>;
  const doneUpTo = states.lastIndexOf('done');
  const span = cycle.milestones.length - 1;
  // The line runs between the first and the last marker's centres; the done part is solid.
  const edge = `${50 / cycle.milestones.length}%`;
  const undated = cycle.milestones.every((milestone) => milestone.date === null);
  const doneShare = span > 0 && doneUpTo >= 0 ? (doneUpTo + (firstAhead > doneUpTo ? 0.5 : 0)) / span : 0;

  return (
    <div className="relative px-2 pt-2 pb-3">
      <div aria-hidden className="absolute top-[1.0625rem] h-0.5 bg-[var(--veritant-royal-15)]" style={{ left: edge, right: edge }}>
        <div className="h-full bg-[var(--color-sapphire)]" style={{ width: `${Math.min(1, doneShare) * 100}%` }} />
      </div>
      <ol className="relative grid" style={{ gridTemplateColumns: `repeat(${cycle.milestones.length}, minmax(0, 1fr))` }}>
        {cycle.milestones.map((milestone, index) => {
          const state = states[index]!;
          return (
            <li key={milestone.key} data-milestone={milestone.key} data-state={state} className="grid justify-items-center gap-1 px-1 text-center">
              <span className="flex h-5 items-center">
                <MilestoneMark state={state} />
              </span>
              <span
                className={cn(
                  'text-[0.75rem] leading-tight',
                  state === 'next' ? 'font-semibold text-[var(--color-royal-blue)]' : 'text-foreground',
                  state === 'done' && 'text-muted-foreground'
                )}
              >
                {milestone.label}
              </span>
              <span className="text-[0.6875rem] text-muted-foreground tabular-nums">
                {milestone.date ? formatDay(milestone.date) : 'date not set'}
                {state === 'next' && cycle.next ? ` · ${inDays(cycle.next.daysLeft)}` : ''}
              </span>
            </li>
          );
        })}
      </ol>
      {undated && can('cycles.manage') && (
        <p data-home-cycle-empty="" className="mt-2 text-center text-[0.75rem]">
          <Link to={TEAM_PATHS.cycles} className="text-[var(--color-sapphire)] hover:underline">
            Set dates in Cycles →
          </Link>
        </p>
      )}
    </div>
  );
}

/** Done a filled mark with a tick, next a ring with a dot, upcoming an empty ring — read by shape,
 *  not by colour alone; the state is said to a screen reader too. */
function MilestoneMark({ state }: { state: MilestoneState }) {
  if (state === 'done') {
    return (
      <span className="flex size-4 items-center justify-center rounded-full bg-[var(--color-sapphire)] ring-2 ring-white">
        <Check className="size-3 text-white" strokeWidth={3} aria-hidden />
        <span className="sr-only">done</span>
      </span>
    );
  }
  if (state === 'next') {
    return (
      <span className="flex size-5 items-center justify-center rounded-full border-2 border-[var(--color-sapphire)] bg-white ring-2 ring-white">
        <span className="size-2 rounded-full bg-[var(--color-sapphire)]" />
        <span className="sr-only">next</span>
      </span>
    );
  }
  return (
    <span className="size-3.5 rounded-full border-2 border-[var(--veritant-royal-55)] bg-white ring-2 ring-white">
      <span className="sr-only">upcoming</span>
    </span>
  );
}

// ---- Pipeline by survey --------------------------------------------------------------------------

const STAGES: { key: Exclude<keyof IDashboardPipeline, 'survey'>; label: string }[] = [
  { key: 'registered', label: 'Registered' },
  { key: 'started', label: 'Started' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'eligible', label: 'Eligible' },
  { key: 'ddDone', label: 'DD done' },
  { key: 'figuresReceived', label: 'Figures in' }
];

function Pipeline({ rows }: { rows: IDashboardPipeline[] }) {
  if (rows.length === 0) return <Empty>No survey has a pipeline this cycle.</Empty>;
  return (
    <div className="overflow-x-auto px-4 pb-3">
      <table className="w-full text-[0.8125rem]">
        <caption className="sr-only">Each survey's count at each stage, and its share of the registered</caption>
        <thead>
          <tr className="text-left text-[0.6875rem] text-muted-foreground">
            <th scope="col" className="py-1 pr-3 font-normal">
              Survey
            </th>
            {STAGES.map((stage) => (
              <th key={stage.key} scope="col" className="px-2 py-1 text-right font-normal whitespace-nowrap">
                {stage.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.survey} data-pipeline={row.survey} className="border-t border-border">
              <th scope="row" className="py-1.5 pr-3 text-left font-medium text-[var(--color-royal-blue)] whitespace-nowrap">
                {listSurveyLabel(row.survey)}
              </th>
              {STAGES.map((stage) => (
                <td key={stage.key} className="px-2 py-1.5 align-middle">
                  <StageCell row={row} stage={stage} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StageCell({ row, stage }: { row: IDashboardPipeline; stage: (typeof STAGES)[number] }) {
  const value = row[stage.key];
  const number = <span className="block text-right tabular-nums">{value.toLocaleString('en-US')}</span>;
  if (stage.key === 'registered') return number;
  const share = row.registered > 0 ? value / row.registered : 0;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="grid min-w-14 gap-1">
          {number}
          <InlineBar share={share} />
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <span className="font-semibold tabular-nums">{value.toLocaleString('en-US')}</span> {stage.label.toLowerCase()} in{' '}
        {listSurveyLabel(row.survey)} — {row.registered > 0 ? `${Math.round(share * 100)}%` : 'none'} of{' '}
        {row.registered.toLocaleString('en-US')} registered
      </TooltipContent>
    </Tooltip>
  );
}

// ---- Activity, last 14 days ----------------------------------------------------------------------

function sum(values: readonly number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

/** The last 7 days against the 7 before: a short mark beside the total, its words for a reader. */
function weekChange(values: readonly number[]): { mark: string; words: string } {
  const last = sum(values.slice(-7));
  const before = sum(values.slice(-14, -7));
  if (before === 0) {
    return last === 0
      ? { mark: '', words: 'none in either week' }
      : { mark: 'new', words: 'none the week before' };
  }
  const change = Math.round(((last - before) / before) * 100);
  if (change === 0) return { mark: '±0%', words: 'same as the week before' };
  const mark = `${change > 0 ? '▲ +' : '▼ '}${change}%`;
  return { mark, words: `${mark} vs the week before` };
}

function Activity({ activity }: { activity: IDashboardActivity }) {
  const panels: { key: string; title: string; series: ISparkSeries[] }[] = [
    { key: 'submissions', title: 'Submissions', series: [{ label: 'submissions', values: activity.submissions }] },
    { key: 'advisor-edits', title: 'Advisor edits', series: [{ label: 'advisor edits', values: activity.advisorEdits }] },
    {
      key: 'compliance',
      title: 'Compliance',
      series: [
        { label: 'raised', values: activity.complianceRaised },
        { label: 'reviewed', values: activity.complianceReviewed }
      ]
    }
  ];
  return (
    <ul className="grid gap-1 px-4 pb-2.5">
      {panels.map((panel) => (
        <li
          key={panel.key}
          data-activity={panel.key}
          className="grid grid-cols-[10rem_minmax(0,1fr)] items-center gap-3 border-t border-border pt-1.5 first:border-t-0"
        >
          <div className="grid">
            <span className="text-[0.75rem] text-muted-foreground">{panel.title}</span>
            {panel.series.map((one, index) => {
              const change = weekChange(one.values);
              return (
                <span key={one.label} className="flex items-baseline gap-1.5 whitespace-nowrap" title={change.words}>
                  {panel.series.length > 1 && <LineKey index={index} className="self-center" />}
                  <span className="text-[1.125rem] leading-tight font-medium text-[var(--color-royal-blue)]">
                    {sum(one.values).toLocaleString('en-US')}
                  </span>
                  {panel.series.length > 1 && <span className="text-[0.75rem] text-muted-foreground">{one.label}</span>}
                  <span className="text-[0.6875rem] text-muted-foreground">
                    <span aria-hidden>{change.mark}</span>
                    <span className="sr-only">{change.words}</span>
                  </span>
                </span>
              );
            })}
          </div>
          <Sparkline
            days={activity.days}
            series={panel.series}
            label={`${panel.title} per day, the last 14 days`}
            height={panel.series.length > 1 ? 44 : 32}
          />
        </li>
      ))}
    </ul>
  );
}

// ---- Team workload -------------------------------------------------------------------------------

/** Who holds what. Number checks' column is drawn where survey-api counts them: an API before
 *  SR-487 sends no `identityChecks` on any row. */
function Workload({ rows }: { rows: IDashboardWorkload[] }) {
  const can = useCan();
  if (rows.length === 0) return <Empty>Nobody holds open work.</Empty>;
  const checks = rows.some((row) => row.identityChecks !== undefined);
  const total = (row: IDashboardWorkload) =>
    row.ddCalls + row.compliance + (row.identityChecks ?? 0) + row.reinstatement + row.metrics;
  const most = Math.max(1, ...rows.map(total));
  return (
    <div className="overflow-x-auto px-4 pb-3">
      <table className="w-full text-[0.8125rem]">
        <caption className="sr-only">Open work per person, with the overdue among it</caption>
        <thead>
          <tr className="text-left text-[0.6875rem] text-muted-foreground">
            <th scope="col" className="py-1 pr-3 font-normal">
              Person
            </th>
            <th scope="col" className="px-2 py-1 font-normal">
              DD calls
            </th>
            <th scope="col" className="px-2 py-1 font-normal">
              Compliance
            </th>
            {checks && (
              <th scope="col" className="px-2 py-1 text-right font-normal">
                Number checks
              </th>
            )}
            <th scope="col" className="px-2 py-1 text-right font-normal">
              Reinstatement
            </th>
            <th scope="col" className="px-2 py-1 text-right font-normal">
              Metrics
            </th>
            <th scope="col" className="w-28 py-1 pl-2 font-normal">
              All open
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const unassigned = row.accountId === null;
            return (
              <tr key={row.accountId ?? 'unassigned'} data-workload={row.accountId ?? 'unassigned'} className="border-t border-border">
                <th
                  scope="row"
                  className={cn(
                    'max-w-48 truncate py-1.5 pr-3 text-left font-medium',
                    unassigned ? 'text-muted-foreground italic' : 'text-[var(--color-royal-blue)]'
                  )}
                >
                  {unassigned ? 'Unassigned' : row.name}
                </th>
                <td className="px-2 py-1.5">
                  <CountWithOverdue
                    count={row.ddCalls}
                    overdue={row.ddOverdue}
                    to={unassigned && can(OPENS.ddCalls) ? `${TEAM_PATHS.ddCalls}?whose=unassigned&${DD_EVERY_SURVEY}` : null}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <CountWithOverdue
                    count={row.compliance}
                    overdue={row.complianceOverdue}
                    to={`${TEAM_PATHS.compliance}?assignee=${encodeURIComponent(row.accountId ?? 'none')}`}
                  />
                </td>
                {checks && (
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {row.identityChecks !== undefined && (
                      <CountLink
                        count={row.identityChecks}
                        to={`${TEAM_PATHS.numberChecks}?assignee=${encodeURIComponent(row.accountId ?? 'none')}`}
                      />
                    )}
                  </td>
                )}
                <td className="px-2 py-1.5 text-right tabular-nums">{row.reinstatement.toLocaleString('en-US')}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  <CountLink count={row.metrics} to={unassigned ? `${TEAM_PATHS.metricsUpdates}?whose=none` : null} />
                </td>
                <td className="py-1.5 pl-2">
                  <span className="grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-2">
                    <span className="text-right tabular-nums">{total(row).toLocaleString('en-US')}</span>
                    <InlineBar share={total(row) / most} />
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CountLink({ count, to }: { count: number; to: Nullable<string> }) {
  const text = count.toLocaleString('en-US');
  if (to === null || count === 0) return <span className="tabular-nums">{text}</span>;
  return (
    <Link to={to} className="text-[var(--color-sapphire)] tabular-nums hover:underline">
      {text}
    </Link>
  );
}

function CountWithOverdue({ count, overdue, to }: { count: number; overdue: number; to: Nullable<string> }) {
  return (
    <span className="flex items-center gap-1.5 whitespace-nowrap">
      <span className="min-w-6 text-right">
        <CountLink count={count} to={to} />
      </span>
      {overdue > 0 && <SignalBadge signal={signal('overdue', 'overdue', 'danger', overdue)} />}
    </span>
  );
}

/** A link where `to` is one, else its words alone. */
function LinkIf({ to, className, children }: { to: Nullable<string>; className?: string; children: React.ReactNode }) {
  return to === null ? (
    <span className={className}>{children}</span>
  ) : (
    <Link to={to} className={cn(className, 'hover:underline')}>
      {children}
    </Link>
  );
}

// ---- Data freshness ------------------------------------------------------------------------------

const REFRESH_CHIPS: Record<string, { label: string; tone: ChipTone }> = {
  completed: { label: 'Completed', tone: 'success' },
  running: { label: 'Running', tone: 'info' },
  paused: { label: 'Paused', tone: 'attention' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  failed: { label: 'Failed', tone: 'danger' }
};

const TONE_ICON: Record<ChipTone, React.ComponentType<{ className?: string }>> = {
  success: CircleCheck,
  info: LoaderCircle,
  attention: CirclePause,
  danger: CircleAlert,
  neutral: Ban,
  mock: CircleDot
};

/** A state as its chip with an icon beside the word — never colour alone. */
function StateWithIcon({ label, tone }: { label: string; tone: ChipTone }) {
  const Icon = TONE_ICON[tone];
  return (
    <StatusChip tone={tone}>
      <Icon className="mr-1 inline size-3.5 align-[-0.1875rem]" aria-hidden />
      {label}
    </StatusChip>
  );
}

/** How long ago an instant was, in the largest whole unit. */
function ago(instant: string): string {
  const ms = Date.now() - Date.parse(instant);
  if (Number.isNaN(ms)) return '';
  const minutes = Math.max(0, Math.round(ms / 60000));
  if (minutes < 60) return minutes <= 1 ? 'just now' : `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

function Freshness({ freshness }: { freshness: IDashboardFreshness }) {
  const can = useCan();
  const refresh = freshness.brokerCheck;
  const imported = freshness.lastImport;
  const refreshChip = refresh ? (REFRESH_CHIPS[refresh.status] ?? { label: refresh.status, tone: 'neutral' as const }) : null;
  const importChip = imported ? importStateChip(imported.status) : null;
  return (
    <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 px-4 pb-3 text-[0.8125rem]">
      <dt className="pt-0.5 text-muted-foreground">
        <LinkIf to={can(OPENS.refreshRuns) ? TEAM_PATHS.refreshRuns : null}>
          BrokerCheck refresh
        </LinkIf>
      </dt>
      <dd data-freshness="brokercheck" className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {refresh && refreshChip ? (
          <>
            <StateWithIcon label={refreshChip.label} tone={refreshChip.tone} />
            <span className="text-muted-foreground">
              last run {changedWhen(refresh.lastRunAt).replace(/^at /, 'today at ')} · {ago(refresh.lastRunAt)}
            </span>
          </>
        ) : (
          <span className="text-muted-foreground">Never run</span>
        )}
      </dd>
      <dt className="pt-0.5 text-muted-foreground">
        <LinkIf to={can(OPENS.imports) ? TEAM_PATHS.imports : null}>
          Last import
        </LinkIf>
      </dt>
      <dd data-freshness="import" className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {imported && importChip ? (
          <>
            <StateWithIcon label={importChip.label} tone={importChip.tone} />
            <span className="text-muted-foreground">
              {changedWhen(imported.at).replace(/^at /, 'today at ')} · {ago(imported.at)}
            </span>
          </>
        ) : (
          <span className="text-muted-foreground">No import yet</span>
        )}
      </dd>
    </dl>
  );
}

// ---- Rankings ------------------------------------------------------------------------------------

function Rankings({ rows }: { rows: IDashboardRanking[] }) {
  const can = useCan();
  if (rows.length === 0) return <Empty>No ranking this cycle yet.</Empty>;
  return (
    <ul className="grid px-4 pb-2 text-[0.8125rem]">
      {rows.map((row) => (
        <li
          key={row.rankingId}
          data-ranking={row.rankingId}
          className="grid grid-cols-[minmax(0,1fr)_auto_9.5rem] items-center gap-3 border-t border-border py-1.5 first:border-t-0"
        >
          <LinkIf
            to={can(OPENS.rankings) ? TEAM_PATHS.rankings : null}
            className="truncate font-medium text-[var(--color-royal-blue)]"
          >
            {row.name.includes(String(row.year)) ? row.name : `${row.name} ${row.year}`}
          </LinkIf>
          <RankingStatusChip status={row.status} />
          <span className="text-right text-[0.75rem] text-muted-foreground tabular-nums">
            {row.publishOn ? `publishes ${formatDay(row.publishOn)}` : 'no publish date'}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---- The "?" -------------------------------------------------------------------------------------

function DashboardHelp() {
  return (
    <HelpPopover label="About Home" align="end">
      <p>The team's dashboard, the same for everyone; <em>My tasks</em> counts your own. Each block:</p>
      <dl className="grid gap-1.5 text-[0.8125rem]">
        <HelpEntry term="Queues">
          Your own tasks, then each work page's count and signals, as its own cards show them; the toggle
          sizes every page's cards.
        </HelpEntry>
        <HelpEntry term="Cycle">Its milestones: done, the next one, then what is still ahead.</HelpEntry>
        <HelpEntry term="Pipeline by survey">
          How many reached each stage this cycle, with the share of those registered.
        </HelpEntry>
        <HelpEntry term="Activity">
          Per day over the last 14 days and the total, with the last 7 days against the 7 before
          (<em>new</em>: none the week before). Hover or use the arrow keys for a day.
        </HelpEntry>
        <HelpEntry term="Team workload">
          Open work per person, overdue beside it; a linked count opens its page narrowed to them.
        </HelpEntry>
        <HelpEntry term="Recent records">The records you last opened on this browser.</HelpEntry>
        <HelpEntry term="Data freshness">The last BrokerCheck refresh and list import, and how each ended.</HelpEntry>
        <HelpEntry term="Rankings">This cycle's rankings, their status and publish date.</HelpEntry>
      </dl>
      <p>Anything else is a search away: the box at the top of the window, or Ctrl/⌘ K.</p>
    </HelpPopover>
  );
}

function HelpEntry({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="font-medium">{term}</dt>
      <dd className="text-muted-foreground">{children}</dd>
    </div>
  );
}
